// ComplianceChecklistView.jsx - Without Pest Control & Oil (File-only types removed)
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Button,
  Flex,
  List,
  Spin,
  Input,
  Popover,
  Modal,
  Slider,
  Image,
  Empty,
  Alert,
} from "antd";
import {
  CheckOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  FormOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import "./style.css";
import { toast } from "react-toastify";
import ChecklistTaskNote from "../../components/ChecklistTaskNote";
import {
  useFetchSelfRestaurantDataQuery,
  useUpdateRestaurantSettingMutation,
} from "../restaurant/restaruant.service";
import {
  useCreateComplianceSubmissionMutation,
  useFetchCompliancesWithSubmissionQuery,
} from "./compliance.service";
import { BACKEND_URL } from "../../constant";

const ComplianceChecklistView = () => {
  const { t, i18n } = useTranslation();
  const { type } = useParams();
  const languageMap = { zh: "ch", pl: "po", tr: "tu", ja: "jp" };
  const curLanguage = languageMap[i18n.language] || i18n.language;
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalId, setIsModalId] = useState();
  const [modalContent, setModalContent] = useState();
  const [contents, setContents] = useState({});
  const [checkTempSlider, setCheckTempSlider] = useState({});
  const [openPopoverId, setOpenPopoverId] = useState(null);

  const {
    data = { data: { compliances: [] } },
    isFetching,
    isSuccess,
    refetch,
  } = useFetchCompliancesWithSubmissionQuery(type, {
    refetchOnMountOrArgChange: true,
  });
  const { compliances = [] } = data.data || {};

  const {
    data: restaurantData = {
      data: { restaurant: { complianceLevel: [], manualComplianceSections: [] } },
    },
  } = useFetchSelfRestaurantDataQuery("", { refetchOnMountOrArgChange: true });

  const {
    data: {
      restaurant: { complianceLevel = [], manualComplianceSections = [] },
    },
  } = restaurantData;

  const isManualSection = manualComplianceSections.includes(type);

  const [requestCreateChecklistUpdated, { isLoading: isSubmitting }] =
    useCreateComplianceSubmissionMutation();

  const [
    requestUpdateRestaurantSetting,
    { isLoading: isUpdateLoading, isSuccess: isUpdateSuccess },
  ] = useUpdateRestaurantSettingMutation();

  // ===== Helper function to get localized title =====
  const getLocalizedTitle = (item) => {
    if (!item) return "";
    if (item.title && typeof item.title === "object") {
      return item.title[curLanguage] || item.title["default"] || "";
    }
    if (item.title && typeof item.title === "string") {
      return item.title;
    }
    return item[`title_${curLanguage}`] || item["title"] || "";
  };

  // ===== Helper function to get localized content =====
  const getLocalizedContent = (item) => {
    if (!item?.submissionData) return "";

    if (
      item.submissionData.content &&
      typeof item.submissionData.content === "object"
    ) {
      return (
        item.submissionData.content[curLanguage] ||
        item.submissionData.content["default"] ||
        ""
      );
    }
    if (
      item.submissionData.content &&
      typeof item.submissionData.content === "string"
    ) {
      return item.submissionData.content;
    }
    return (
      item.submissionData[`content_${curLanguage}`] ||
      item.submissionData["content"] ||
      ""
    );
  };

  // ===== Helper function to get localized comment =====
  const getLocalizedComment = (item) => {
    if (!item?.submissionData) return "";

    if (
      item.submissionData.comment &&
      typeof item.submissionData.comment === "object"
    ) {
      return (
        item.submissionData.comment[curLanguage] ||
        item.submissionData.comment["default"] ||
        ""
      );
    }
    if (
      item.submissionData.comment &&
      typeof item.submissionData.comment === "string"
    ) {
      return item.submissionData.comment;
    }
    return (
      item.submissionData[`comment_${curLanguage}`] ||
      item.submissionData["comment"] ||
      ""
    );
  };

  const handleCompleteChecklist = async (payload) => {
    try {
      await requestCreateChecklistUpdated(payload).unwrap();
      await refetch();
    } catch (error) {
      console.error("Failed to update compliance task:", error);
      toast.error(error?.data?.message || "Failed to update checklist");
    }
  };

  const hasTaskNote = (item) => {
    const comment = getLocalizedComment(item);
    return Boolean(comment && String(comment).trim());
  };

  const isTaskDone = (item) =>
    Boolean(item.submissionData?.isCompleted) || hasTaskNote(item);

  useEffect(() => {
    if (!isModalOpen) {
      setIsModalId();
      setModalContent();
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (isSuccess && compliances.length > 0) {
      setCheckTempSlider(
        compliances.reduce((obj, cur) => {
          obj[cur._id] = cur.submissionData?.temperature || 0;
          return obj;
        }, {})
      );
    }
  }, [compliances, isSuccess]);

  useEffect(() => {
    if (isUpdateSuccess) {
      navigate("/compliance_checks");
    }
  }, [isUpdateLoading]);

  const isEmpty = !compliances || compliances.length === 0;

  return (
    <Spin spinning={isFetching || isSubmitting}>
      <Card
        title={t(type) || type}
        className="opencheck-list-page"
        extra={
          <Flex justify="center">
            <Button onClick={() => navigate("/compliance_checks")}>
              {t("Back")}
            </Button>
          </Flex>
        }
        bordered={false}
      >
        {isManualSection && (
          <Alert
            type="success"
            showIcon
            className="compliance-manual-banner"
            message={t("Manually completed")}
            description={t(
              "This section was marked as completed on paper. You can still review the guidance below.",
            )}
          />
        )}
        {isEmpty ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span>
                No tasks available for <strong>{t(type) || type}</strong>
                <br />
                <span style={{ fontSize: '12px', color: '#999' }}>
                  Please add tasks from the admin panel
                </span>
              </span>
            }
          />
        ) : (
          <>
            <List
              bordered
              dataSource={compliances}
              renderItem={(item, key) => {
                const titleText = getLocalizedTitle(item);
                const contentText = getLocalizedContent(item);
                const commentText = getLocalizedComment(item);
                const isCompleted = item.submissionData?.isCompleted || false;

                return (
                  <List.Item
                    key={key}
                    className={item.isSection && "background-section"}
                  >
                    {item.isSection ? (
                      <Row className="opencheck-list-page-list-item">
                        <Col span={24} className="opencheck-list-page-title">
                          <div className="opencheck-list-page-title-description">
                            <div
                              className="compliance-checklist-content"
                              dangerouslySetInnerHTML={{
                                __html: titleText,
                              }}
                            />
                          </div>
                        </Col>
                      </Row>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                      >
                        <Row className="opencheck-list-page-list-item">
                          <Col span={24} className="opencheck-list-page-title">
                            <div className="opencheck-list-page-title-label">
                              <div
                                className="compliance-checklist-content"
                                dangerouslySetInnerHTML={{
                                  __html: titleText,
                                }}
                              />
                            </div>
                            <ChecklistTaskNote
                              comment={commentText}
                              language={curLanguage}
                            />
                            <div className="compliance-list-page-title-description">
                              {item.isInput && (
                                <div style={{ padding: "5px 10px" }}>
                                  <Input
                                    defaultValue={contentText}
                                    style={{
                                      background: "transparent",
                                      maxWidth: "500px",
                                    }}
                                    onChange={(c) =>
                                      setContents((e) => ({
                                        ...e,
                                        [item._id]: c.target.value,
                                      }))
                                    }
                                  />
                                </div>
                              )}
                              {item.isDateRecord && (
                                <div style={{ padding: "0 10px" }}>
                                  *RECORD TIME AND DATE AUTOMATICALLY*
                                </div>
                              )}
                              {item.isImage && item.imageUrl && (
                                <img
                                  crossOrigin="anonymous"
                                  src={`${BACKEND_URL}${item.imageUrl}`}
                                  alt="Uploaded compliance image"
                                  style={{
                                    objectFit: "cover",
                                    border: "1px solid #d9d9d9",
                                    borderRadius: "6px",
                                    width: "50%",
                                    height: "auto",
                                  }}
                                />
                              )}
                              {item.isTempSlider && (
                                <>
                                  <div
                                    style={{
                                      fontSize: "1.5rem",
                                      textAlign: "center",
                                      marginTop: 10,
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {checkTempSlider[item._id]
                                      ? checkTempSlider[item._id].toFixed(1)
                                      : 0}
                                    °C
                                  </div>
                                  <Slider
                                    className="temp-slider-width"
                                    min={-30}
                                    max={120}
                                    step={0.1}
                                    styles={{
                                      track: {
                                        background: "transparent",
                                      },
                                      tracks: {
                                        background: `rgb(109, 179, 63)`,
                                      },
                                    }}
                                    onChange={(temp) =>
                                      setCheckTempSlider((e) => ({
                                        ...e,
                                        [item._id]: temp,
                                      }))
                                    }
                                    value={
                                      checkTempSlider[item._id]
                                        ? checkTempSlider[item._id]
                                        : 0
                                    }
                                  />
                                </>
                              )}
                            </div>
                          </Col>
                        </Row>
                        <Row className="opencheck-list-page-list-item">
                          <Col span={10} />
                          <Col span={2} className="opencheck-list-page-complete">
                            {isCompleted ? (
                              <CheckOutlined
                                className="checklist-complete"
                                onClick={() => {
                                  handleCompleteChecklist({
                                    compliance_id: item._id,
                                    type,
                                    isCompleted: false,
                                    content:
                                      contents[item._id] ?? contentText,
                                    temperature:
                                      (checkTempSlider[item._id] ?? 
                                      item.submissionData?.temperature) || 0,
                                  });
                                }}
                              />
                            ) : commentText ? (
                              <SwapOutlined
                                className="checklist-complete"
                                onClick={() => {
                                  handleCompleteChecklist({
                                    compliance_id: item._id,
                                    type,
                                    isCompleted: false,
                                    comment: "",
                                    content:
                                      contents[item._id] ?? contentText,
                                    temperature:
                                      (checkTempSlider[item._id] ?? 
                                      item.submissionData?.temperature) || 0,
                                  });
                                }}
                              />
                            ) : (
                              <CheckOutlined
                                className="checklist-pending"
                                onClick={() => {
                                  if (item.isInput) {
                                    const userContent = contents[item._id]?.trim();
                                    const existingContent = contentText.trim();
                                    if (!userContent && !existingContent) {
                                      toast.error(
                                        "Please enter content before completing."
                                      );
                                      return;
                                    }
                                  }
                                  handleCompleteChecklist({
                                    compliance_id: item._id,
                                    type,
                                    isCompleted: true,
                                    content:
                                      contents[item._id] ?? contentText,
                                    temperature:
                                      (checkTempSlider[item._id] ?? 
                                      item.submissionData?.temperature) || 0,
                                  });
                                }}
                              />
                            )}
                          </Col>
                          <Col span={2} className="opencheck-list-page-tools">
                            <Popover
                              placement="right"
                              content={
                                <div className="opencheck-list-page-tools-popover">
                                  {!isCompleted && !commentText && (
                                    <Button
                                      variant="solid"
                                      color="pink"
                                      size="small"
                                      icon={<FormOutlined />}
                                      onClick={() => {
                                        setIsModalOpen(true);
                                        setIsModalId(item._id);
                                        setOpenPopoverId(null);
                                      }}
                                    >
                                      {t("Add Note")}
                                    </Button>
                                  )}
                                </div>
                              }
                              open={openPopoverId === item._id}
                              onOpenChange={(visible) =>
                                setOpenPopoverId(visible ? item._id : null)
                              }
                              trigger="click"
                            >
                              <MoreOutlined className="opencheck-list-page-tools-icon" />
                            </Popover>
                          </Col>
                          <Col span={10} />
                        </Row>
                      </div>
                    )}
                  </List.Item>
                );
              }}
            />
            <Flex justify="center" style={{ marginTop: "10px" }}>
              <Button
                type="primary"
                loading={isUpdateLoading}
                disabled={
                  compliances.filter((item) => !item.isSection).length !==
                  compliances.filter(
                    (item) => !item.isSection && isTaskDone(item)
                  ).length
                }
                onClick={() => {
                  const taskCount = compliances.filter(
                    (item) => !item.isSection
                  ).length;
                  const completedCount = compliances.filter(
                    (item) => !item.isSection && isTaskDone(item)
                  ).length;

                  if (taskCount === completedCount) {
                    if (!complianceLevel.includes(type)) {
                      requestUpdateRestaurantSetting({
                        complianceLevel: [...complianceLevel, type],
                        manualComplianceSections:
                          manualComplianceSections.filter(
                            (section) => section !== type
                          ),
                      });
                    } else navigate("/compliance_checks");
                  } else {
                    toast.warning("Please complete all checklist");
                  }
                }}
              >
                {t("Submit")}
              </Button>
            </Flex>
          </>
        )}
        
        <Modal
          title="Reason"
          open={isModalOpen}
          onOk={async () => {
            if (modalContent && modalContent !== "") {
              await handleCompleteChecklist({
                compliance_id: isModalId,
                type,
                isCompleted: false,
                comment: modalContent,
              });
              setIsModalOpen(false);
              setModalContent("");
            } else {
              navigate("/home");
            }
          }}
          onCancel={() => setIsModalOpen(false)}
        >
          <Input.TextArea
            onChange={(e) => setModalContent(e.target.value)}
            value={modalContent}
            autoSave={{ min: 3, max: 3 }}
          />
        </Modal>
      </Card>
    </Spin>
  );
};

export default ComplianceChecklistView;